import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import Card from '@/components/elements/Card/Card';
import { Button } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { FILTER_OPTIONS, getSelectedFilters } from '@/features/searchV2/components/Filter/Filter.helpers';
import { FilterManagerOutput, FilterOption } from '@/features/searchV2/components/Filter/Filter.types';
import { classnames, isServer } from '@/helpers';
import { SearchType } from '@/hooks/search/useSearch.helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s, _t } from '@/locale';
import { FILTER_ID } from '@/types/state/search';
import { createPortal } from 'react-dom';
import { Fragment } from 'react/jsx-runtime';

type FilterProps = FilterManagerOutput & {
  searchType: SearchType;
};

const Filter = (props: FilterProps) => {
  const { isMobileView } = useMobileView();
  const selectedFilters = getSelectedFilters(props.prefs);

  const appliedFilters = (props.urlPrefs || '')
    .split(',')
    .filter(Boolean)
    .map((pref) => pref && +pref);

  if (isMobileView) {
    return (
      <Fragment>
        <Button
          onClick={() => props.onShowOptions(true)}
          variant="link"
          rightIcon={<Icon color="fg_link" variant="filter" style={{ width: 20, height: 20 }} />}>
          <span>
            {_t('filters')}
            {appliedFilters.length > 0 && <span className="">({appliedFilters.length})</span>}
          </span>
        </Button>
        {props.showOptions &&
          !isServer &&
          createPortal(
            <Fragment>
              {/*  must beat the map carousel's z-9999 */}
              <div className="bg-scrim-40 fixed inset-0 z-[10000]"></div>
              <Card
                bottom
                closable={false}
                onClickOutside={() => props.onShowOptions(false)}
                className="fixed bottom-0 left-0 right-0 z-[10001] text-left">
                <span className="text-capitalize text-xxs text-fg_secondary pb-md block">{_s('filterBy')}</span>
                <ul className="space-y-sm pb-2xl">
                  <FilterOptions
                    type={props.searchType}
                    selectedFilters={selectedFilters}
                    onChange={(filterOption) => props.onToggleFilterOption(filterOption)}
                    isMobileView={isMobileView}
                  />
                </ul>
                <Button onClick={props.onApplyFilters}>{_t('seeResults')}</Button>
              </Card>
            </Fragment>,
            document.body,
          )}
      </Fragment>
    );
  }

  return (
    <div role="group" className="relative" aria-label={''}>
      <DropdownMenu
        initialState={{ isOpen: props.showOptions }}
        preventScrollFocus
        onStateChange={(isOpen) => props.onShowOptions(isOpen)}>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="link"
            rightIcon={<Icon color="fg_link" variant="filter" style={{ width: 20, height: 20 }} />}>
            <span>
              {_t('filters')}
              {appliedFilters.length > 0 && <span className="">({appliedFilters.length})</span>}
            </span>
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          className={classnames(
            'p-xs space-y-2xs bg-bg_neutral',
            'border-border_focus -mx-[3px] rounded-md border-[3px]',
          )}>
          <FilterOptions
            type={props.searchType}
            selectedFilters={selectedFilters}
            onChange={(filterOption) => props.onToggleFilterOption(filterOption)}
            isMobileView={isMobileView}
          />
        </DropdownMenu.Content>
      </DropdownMenu>
    </div>
  );
};
const dropdownItemBaseClassnames = 'gap-xs flex items-center h-3xl px-sm rounded-sm';

const FilterOptions = ({
  type,
  selectedFilters,
  isMobileView,
  onChange,
}: {
  type: SearchType;
  selectedFilters: FilterOption[];
  isMobileView: boolean;
  onChange: (filterBy: FilterOption) => void;
}) => {
  return (
    <>
      {FILTER_OPTIONS.filter((option) => {
        if (type === 'deals_sistaminuten') {
          switch (option.id) {
            case FILTER_ID.WELLNESSCARD:
            case FILTER_ID.GIFTCARD:
            case FILTER_ID.PAY_LATER:
            case FILTER_ID.CERTIFIED:
              return true;
            default:
              return false;
          }
        }

        if (type === 'deals' || type === 'brand_deals') {
          switch (option.id) {
            case FILTER_ID.PAY_LATER:
            case FILTER_ID.CERTIFIED:
              return true;
            default:
              return false;
          }
        }

        return true;
      }).map((item, index) => {
        const isActive = selectedFilters.some((filter) => filter.id === item.id);

        if (isMobileView) {
          return (
            <li
              key={index}
              className="gap-sm flex cursor-pointer flex-row-reverse items-center"
              role="checkbox"
              aria-checked={isActive}
              onClick={() => onChange(item)}>
              <span className="flex-1">{item.label}</span>
              {isActive ? (
                <Icon variant="checkbox-checked-filled" className="flex-shrink-0" />
              ) : (
                <Icon variant="square" className="flex-shrink-0" />
              )}
            </li>
          );
        }

        return (
          <DropdownMenu.Item
            closeOnSelect={false}
            onSelect={() => onChange(item)}
            className={classnames(dropdownItemBaseClassnames)}
            key={index}>
            <span className="flex-1">{item.label}</span>
            {isActive ? (
              <Icon variant="checkbox-checked-filled" className="flex-shrink-0" />
            ) : (
              <Icon variant="square" className="flex-shrink-0" />
            )}
          </DropdownMenu.Item>
        );
      })}
    </>
  );
};

export default Filter;
